<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { subDays } from 'date-fns';
import { formatCustomSlaDuration } from 'dashboard/helper/customSlaTimeHelper';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import ReportHeader from './components/ReportHeader.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import CustomSlaBreachTypeChart from './components/CustomSla/CustomSlaBreachTypeChart.vue';
import CustomSlaMetricCards from './components/CustomSla/CustomSlaMetricCards.vue';
import CustomSlaOutcomePieChart from './components/CustomSla/CustomSlaOutcomePieChart.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const isTeam = computed(() => route.name === 'custom_sla_reports_team_show');
const entityId = computed(() => Number(route.params.id));

const entity = isTeam.value
  ? useFunctionGetter('teams/getTeamById', entityId)
  : useFunctionGetter('agents/getAgentById', entityId);

const metrics = useMapGetter('customSlaReports/getMetrics');
const uiFlags = useMapGetter('customSlaReports/getUIFlags');

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRangeType = ref('last7days');

const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const row = computed(() => {
  const rows = isTeam.value ? metrics.value.byTeam : metrics.value.byAgent;
  return rows.find(r => r.id === entityId.value) || null;
});

const entityName = computed(
  () => entity.value?.name || entity.value?.available_name || ''
);

const conversationsCount = computed(() => row.value?.conversations_count || 0);
const avgFirstResponseTime = computed(() =>
  row.value?.avg_first_response_time
    ? formatCustomSlaDuration(row.value.avg_first_response_time, t)
    : '--'
);
const avgResolutionTime = computed(() =>
  row.value?.avg_resolution_time
    ? formatCustomSlaDuration(row.value.avg_resolution_time, t)
    : '--'
);
const hitCount = computed(() => row.value?.hit_count || 0);
const missedCount = computed(() => row.value?.missed_count || 0);
const nextResponseMetCount = computed(
  () => row.value?.next_response_met_count || 0
);
const nextResponseMissedCount = computed(
  () => row.value?.next_response_missed_count || 0
);

const fetchMetrics = () => {
  store.dispatch('customSlaReports/getMetrics', {
    from: from.value,
    to: to.value,
    teamId: isTeam.value ? entityId.value : undefined,
    assignedAgentId: isTeam.value ? undefined : entityId.value,
  });
};

const onDateRangeChange = ([startDate, endDate, rangeType]) => {
  customDateRange.value = [startDate, endDate];
  selectedDateRangeType.value = rangeType;
  fetchMetrics();
};

onMounted(() => {
  store.dispatch(isTeam.value ? 'teams/get' : 'agents/get');
  fetchMetrics();
});
</script>

<template>
  <ReportHeader
    :header-title="entityName || $t('CUSTOM_SLA_REPORTS.HEADER')"
    has-back-button
  />
  <div class="flex flex-col flex-1 gap-6">
    <WootDatePicker
      v-model:date-range="customDateRange"
      v-model:range-type="selectedDateRangeType"
      @date-range-changed="onDateRangeChange"
    />

    <woot-loading-state
      v-if="uiFlags.isFetchingMetrics"
      :message="$t('CUSTOM_SLA_REPORTS.LOADING')"
    />
    <template v-else>
      <CustomSlaMetricCards
        :conversations-count="conversationsCount"
        :avg-first-response-time="avgFirstResponseTime"
        :avg-resolution-time="avgResolutionTime"
      />
      <CustomSlaBreachTypeChart
        :row="row"
        :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
      />
      <div class="flex flex-col w-full gap-6 sm:flex-row">
        <CustomSlaOutcomePieChart
          :hit-count="hitCount"
          :missed-count="missedCount"
          :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
          class="w-full sm:max-w-sm self-start"
        />
        <CustomSlaOutcomePieChart
          :hit-count="nextResponseMetCount"
          :missed-count="nextResponseMissedCount"
          :title="$t('CUSTOM_SLA_REPORTS.CHARTS.NEXT_RESPONSE_OUTCOME')"
          met-label-key="CUSTOM_SLA_REPORTS.CHARTS.NEXT_RESPONSE_MET"
          missed-label-key="CUSTOM_SLA_REPORTS.CHARTS.NEXT_RESPONSE_MISSED"
          :empty-message="$t('CUSTOM_SLA_REPORTS.NO_DATA')"
          class="w-full sm:max-w-sm self-start"
        />
      </div>
    </template>
  </div>
</template>
